import React, { useEffect, useState } from "react";
import "../Keyword.css";
import getDefination from "../services/getDefination";

function Keyword({ keywords }) {
  const [hoveredWord, setHoveredWord] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [mouseEnter, setMouseEnter] = useState(false);
  const [hoveringWord, setHoveringWord] = useState(null);
  useEffect(() => {
    document.addEventListener("click", onClickOutside);
    return () => {
      document.removeEventListener("click", onClickOutside);
    };
  }, []);

  const onClickOutside = () => {
    setHoveredWord(null);
  };
  if (!keywords) return null;

  // Extract keywords from the HTML string
  const extractKeywords = (htmlString) => {
    const keywordMatches = htmlString.match(/<p class="keyword">(.*?)<\/p>/g);
    if (!keywordMatches) return [];

    return keywordMatches.map((match, index) => {
      const text = match.replace(/<p class="keyword">(.*?)<\/p>/, "$1");
      return { id: index, text: text.trim() };
    });
  };

  // Function to get word definition from API and format it
  const getWordDefinition = async (word) => {
    try {
      setIsLoading(true);
      const response = await getDefination(word);
      setIsLoading(false);
      // Handle Wikipedia API response format
      if (response && response.extract && response.type === "standard") {
        return {
          word:
            response.titles?.normalized || response.titles?.canonical || word,
          phonetic: "", // Wikipedia doesn't provide phonetic info
          partOfSpeech: "", // Wikipedia doesn't provide part of speech
          definition: response.extract,
          shortDescription: response.description || "",
          example: "", // Wikipedia doesn't provide examples in this format
          synonyms: [], // Wikipedia doesn't provide synonyms in this format
          thumbnail: response.thumbnail?.source || null,
          isPresent: true,
          isWikipedia: true,
        };
      }

      // Handle traditional dictionary API response format (fallback)
      if (response && response.length > 0 && response[0].type === "standard") {
        const wordData = response[0];
        const firstMeaning = wordData.meanings?.[0];
        const firstDefinition = firstMeaning?.definitions?.[0];

        return {
          word: wordData.word,
          phonetic: wordData.phonetic || wordData.phonetics?.[0]?.text || "",
          partOfSpeech: firstMeaning?.partOfSpeech || "",
          definition: firstDefinition?.definition || "No definition available",
          shortDescription: "",
          example: firstDefinition?.example || "",
          synonyms: firstMeaning?.synonyms?.slice(0, 3) || [], // Limit to first 3 synonyms
          thumbnail: null,
          isPresent: true,
          isWikipedia: false,
        };
      }
      return {
        word: word,
        phonetic: "",
        partOfSpeech: "",
        definition: `No definition found for "${word}"`,
        shortDescription: "",
        example: "",
        synonyms: [],
        thumbnail: null,
        isPresent: false,
        isWikipedia: false,
      };
    } catch (error) {
      // console.error('Error fetching definition:', error);
      setIsLoading(false);
      return {
        word: word,
        phonetic: "",
        partOfSpeech: "",
        definition: `Error loading definition for "${word}"`,
        shortDescription: "",
        example: "",
        synonyms: [],
        thumbnail: null,
        isPresent: false,
        isWikipedia: false,
      };
    }
  };

  const handleMouseEnter = async (word, event) => {
    // Set tooltip position first
    setMouseEnter(true);
    setHoveringWord(word);
    setTooltipPosition({
      x: event.clientX,
      y: event.clientY - 40,
    });

    // If word contains spaces, first try to get definition for the entire phrase
    if (word.includes(" ")) {
      try {
        const phraseDefinition = await getWordDefinition(word);
        // If phrase has a valid definition, show only that
        if (phraseDefinition.isPresent) {
          setHoveredWord([{ word: word, definition: phraseDefinition }]);
          return;
        }

        // If phrase has no definition, get definitions for individual words
        const wordArray = word.split(" ");
        const definitions = [];

        for (let i = 0; i < wordArray.length; i++) {
          try {
            const definition = await getWordDefinition(wordArray[i]);
            if (definition.isPresent) {
              definitions.push({ word: wordArray[i], definition });
            }
          } catch (error) {
            // Continue with next word if one fails
          }
        }

        setHoveredWord(definitions);
      } catch (error) {
        // If phrase lookup fails, fallback to individual words
        const wordArray = word.split(" ");
        const definitions = [];

        for (let i = 0; i < wordArray.length; i++) {
          try {
            const definition = await getWordDefinition(wordArray[i]);
            if (definition.isPresent) {
              definitions.push({ word: wordArray[i], definition });
            }
          } catch (error) {
            // Continue with next word if one fails
          }
        }

        setHoveredWord(definitions);
      }
    } else {
      // For single words, get definition directly
      try {
        const definition = await getWordDefinition(word);
        if (definition.isPresent) {
          setHoveredWord([{ word: word, definition }]);
        }
      } catch (error) {
        // Handle error case
      }
    }
  };

  const handleMouseLeave = () => {
    setHoveredWord(null);
    setMouseEnter(false);
    setHoveringWord(null);
  };

  const keywordList = extractKeywords(keywords);

  return (
    <div style={{ marginTop: "7px" }}>
      <p className="heading-keyword">Keywords</p>
      <div
        style={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          position: "relative",
        }}
      >
        {keywordList.map((keyword) => (
          <div
            key={keyword.id}
            className="keyword-tittle"
            onMouseEnter={(e) => handleMouseEnter(keyword.text, e)}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: "pointer" }}
          >
            <span className="keyword-text">{keyword.text}</span>
          </div>
        ))}

        {/* Tooltip */}
        {isLoading ? (
          <div
            className="keyword-tooltip"
            style={{
              position: "fixed",
              left: tooltipPosition.x,
              top: tooltipPosition.y,
              zIndex: 1000,
              pointerEvents: "none",
            }}
          >
            Loading...
          </div>
        ) : (hoveredWord  ) ? (
          <div
            className="keyword-tooltip"
            style={{
              position: "fixed",
              left: tooltipPosition.x,
              top: tooltipPosition.y,
              zIndex: 1000,
              pointerEvents: "none",
            }}
          >
            <div key={0} className="tooltip-content">
              <div className="tooltip-header">
                <div className="tooltip-title-section">
                  <strong className="tooltip-word">{hoveringWord}</strong>
                  <span className="tooltip-short-desc">
                    {"No Definition Found"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {hoveredWord && hoveredWord.length > 0 && (
          <div
            className="keyword-tooltip"
            style={{
              position: "fixed",
              left: tooltipPosition.x,
              top: tooltipPosition.y,
              zIndex: 1000,
              pointerEvents: "none",
            }}
          >
            {hoveredWord.map((word, index) => (
              <div key={index} className="tooltip-content">
                <div className="tooltip-header">
                  <div className="tooltip-title-section">
                    <strong className="tooltip-word">
                      {word.definition.word}
                    </strong>
                    {word.definition.shortDescription && (
                      <span className="tooltip-short-desc">
                        {word.definition.shortDescription}
                      </span>
                    )}
                  </div>

                  {word.definition.thumbnail && (
                    <div className="tooltip-image">
                      <img
                        src={word.definition.thumbnail}
                        alt={word.definition.word}
                        className="tooltip-thumbnail"
                      />
                    </div>
                  )}
                </div>

                {!word.definition.isWikipedia && (
                  <div className="tooltip-phonetic-section">
                    {word.definition.phonetic && (
                      <span className="tooltip-phonetic">
                        {word.definition.phonetic}
                      </span>
                    )}
                    {word.definition.partOfSpeech && (
                      <span className="tooltip-part-of-speech">
                        {word.definition.partOfSpeech}
                      </span>
                    )}
                  </div>
                )}

                <div className="tooltip-definition">
                  {word.definition.definition}
                </div>

                {word.definition.example && (
                  <div className="tooltip-example">
                    <em>"{word.definition.example}"</em>
                  </div>
                )}

                {word.definition.synonyms.length > 0 && (
                  <div className="tooltip-synonyms">
                    <strong>Synonyms:</strong>{" "}
                    {word.definition.synonyms.join(", ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Keyword;
